import type { AssignedExercise } from "../classes/regimen";

export type GridCustomizationStatus =
  | "customized"
  | "out_of_date"
  | "source_grid_missing"
  | "up_to_date";

interface GridSource {
  id: string;
  updated?: string;
  exercises?: AssignedExercise[];
  grid?: Array<{ id: string; exercises?: AssignedExercise[] }>;
  sequence?: Array<{ id: string; exercises?: AssignedExercise[] }>;
}

export interface GridSourceLists {
  grid_sets?: GridSource[];
  grid_schedules?: GridSource[];
  grid_sequences?: GridSource[];
}

function snapshotForAssignment(assignment: AssignedExercise): GridSource | null {
  if (assignment.exercise_type === "grid_set") return assignment.grid_set || null;
  if (assignment.exercise_type === "grid_schedule") return assignment.grid_schedule || null;
  if (assignment.exercise_type === "grid_sequence") return assignment.grid_sequence || null;
  return null;
}

function gridSourceExercises(source: GridSource | null): AssignedExercise[] {
  if (!source) return [];
  if (source.exercises) return source.exercises;
  if (source.grid) {
    return source.grid.flatMap((stage) => stage.exercises || []);
  }
  if (source.sequence) {
    return source.sequence.flatMap((step) => step.exercises || []);
  }
  return [];
}

function assignmentLevels(source: GridSource | null): Map<string, number> {
  const levels = new Map<string, number>();
  for (const exercise of gridSourceExercises(source)) {
    if (exercise.level !== null && exercise.level !== undefined) {
      levels.set(exercise.uuid, exercise.level);
    }
  }
  return levels;
}

function restoreAssignmentLevels(source: GridSource, levels: Map<string, number>): void {
  for (const exercise of gridSourceExercises(source)) {
    const level = levels.get(exercise.uuid);
    if (level !== undefined) {
      exercise.level = level;
    }
  }
}

function sourceListForAssignment(
  assignment: AssignedExercise,
  sources: GridSourceLists,
): GridSource[] {
  if (assignment.exercise_type === "grid_set") return sources.grid_sets || [];
  if (assignment.exercise_type === "grid_schedule") return sources.grid_schedules || [];
  if (assignment.exercise_type === "grid_sequence") return sources.grid_sequences || [];
  return [];
}

export function resolveSourceGrid(
  assignment: AssignedExercise,
  sources: GridSourceLists,
): GridSource | null {
  return sourceListForAssignment(assignment, sources)
    .find((source) => source.id === assignment.exercise_id) || null;
}

export function gridCustomizationStatus(
  assignment: AssignedExercise,
  source: GridSource | null,
): GridCustomizationStatus {
  const snapshot = snapshotForAssignment(assignment);
  if (!source || !source.updated || !snapshot || !snapshot.updated) {
    return "source_grid_missing";
  }

  const sourceUpdated = Date.parse(source.updated);
  const snapshotUpdated = Date.parse(snapshot.updated);
  if (!Number.isFinite(sourceUpdated) || !Number.isFinite(snapshotUpdated)) {
    return "source_grid_missing";
  }

  if (assignment.grid_customized) return "customized";

  if (sourceUpdated > snapshotUpdated) {
    return "out_of_date";
  }

  return "up_to_date";
}

/**
 * Remaps an embedded schedule assignment when `grid_current_stage` is missing
 * from the snapshot. Clears the next-stage date so cron/UI recompute from the
 * remapped stage. No-op when the current stage is still valid.
 */
export function ensureValidGridScheduleStage(
  assignment: AssignedExercise,
): AssignedExercise {
  if (assignment.exercise_type !== "grid_schedule" || !assignment.grid_schedule) {
    return assignment;
  }

  const stages = assignment.grid_schedule.grid || [];
  const currentStageExists = stages.some(
    (stage: { id: string }) => stage.id === assignment.grid_current_stage,
  );
  if (currentStageExists) {
    return assignment;
  }

  assignment.grid_current_stage = stages[0]?.id || null;
  assignment.grid_next_stage_date = null;
  return assignment;
}

export function cloneSourceOntoAssignment(
  assignment: AssignedExercise,
  source: GridSource,
): AssignedExercise {
  const levels = assignmentLevels(snapshotForAssignment(assignment));
  const clonedSource = JSON.parse(JSON.stringify(source));
  restoreAssignmentLevels(clonedSource, levels);

  if (assignment.exercise_type === "grid_set") {
    assignment.grid_set = clonedSource;
  } else if (assignment.exercise_type === "grid_schedule") {
    assignment.grid_schedule = clonedSource;
    ensureValidGridScheduleStage(assignment);
  } else if (assignment.exercise_type === "grid_sequence") {
    const steps = clonedSource.sequence || [];
    const activeStepExists = assignment.active_step === "complete"
      || steps.some(
        (step: { id: string }) => step.id === assignment.active_step,
      );
    assignment.grid_sequence = clonedSource;
    if (!activeStepExists) {
      assignment.active_step = steps[0]?.id || "uninitialized";
      assignment.active_step_activation_id = null;
      assignment.active_step_activated_at = null;
    }
  }

  assignment.grid_customized = false;
  return assignment;
}
